// The `AccordionComponent` is a reusable and flexible UI component built with React and Headless UI's `Disclosure`. It allows sections of content to expand and collapse,
// supporting both controlled (`isOpen`) and uncontrolled (`defaultOpen`) usage. The component offers two visual variants (`arrow` and `plusAndMinus`)
// and customizable titles, icons, and buttons. It also supports transitions for smooth open/close animations and can accept custom open/close buttons.
// This accordion is ideal for FAQs, expandable panels, and any UI requiring content toggling behavior.

import React, { ReactNode, useEffect, useState } from 'react';
import { Disclosure, Transition } from '@headlessui/react';
import useClassNames from 'helpers/hooks/useClassNames';
import AccordionButton from './AccordionButton';

export interface AccordionProps {
  index?: number;
  variant?: 'arrow' | 'plusAndMinus';
  accordionListLength?: number;
  className?: string;
  openSectionTitle?: string;
  closedSectionTitle?: string;
  iconClassName?: string;
  buttonClassName?: string;
  buttonWrapperClassName?: string;
  panelClassName?: string;
  sectionTileClassName?: string;
  collapsedLabel?: string;
  customOpenButton?: ReactNode;
  customClosedButton?: ReactNode;
  onClick?: () => void;
  defaultOpen?: boolean;
  isOpen?: boolean;
  onChange?: (isOpen: boolean) => void;
  ref?: React.RefObject<HTMLDivElement>;
}

const AccordionComponent = React.forwardRef<HTMLDivElement, React.PropsWithChildren<AccordionProps>>(
  (
    {
      variant = 'arrow',
      closedSectionTitle,
      openSectionTitle = closedSectionTitle,
      children,
      className = '',
      iconClassName = '',
      buttonClassName = '',
      buttonWrapperClassName = '',
      panelClassName = '',
      sectionTileClassName = '',
      collapsedLabel,
      customClosedButton,
      customOpenButton = customClosedButton,
      defaultOpen = false,
      isOpen,
      onChange,
      onClick,
    },
    ref,
  ) => {
    const panelClassNames = useClassNames([panelClassName]);
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isControlled = isOpen !== undefined;
    const currentOpen = isControlled ? isOpen : internalOpen;

    const handleToggle = () => {
      const newOpen = !currentOpen;
      if (!isControlled) {
        setInternalOpen(newOpen);
      }
      onChange?.(newOpen);
      onClick?.();
    };

    return (
      <div className={className} ref={ref}>
        <Disclosure as="div" defaultOpen={defaultOpen}>
          {({ open: disclosureOpen }) => {
            return (
              <>
                <Disclosure.Button as="div" onClick={handleToggle} className={buttonClassName}>
                  {(currentOpen && !customOpenButton) || (!currentOpen && !customClosedButton) ? (
                    <AccordionButton
                      open={currentOpen}
                      variant={variant}
                      collapsedLabel={collapsedLabel}
                      buttonClassName={buttonClassName}
                      iconClassName={iconClassName}
                      openSectionTitle={openSectionTitle}
                      closedSectionTitle={openSectionTitle}
                      buttonWrapperClassName={buttonWrapperClassName}
                      sectionTileClassName={sectionTileClassName}
                    />
                  ) : currentOpen ? (
                    customOpenButton
                  ) : (
                    customClosedButton
                  )}
                </Disclosure.Button>

                <Transition
                  show={currentOpen}
                  enter="transition duration-150 ease-out"
                  enterFrom="transform scale-y-95 opacity-0"
                  enterTo="transform scale-y-100 opacity-100"
                  leave="transition duration-100 ease-out"
                  leaveFrom="transform scale-y-100 opacity-100"
                  leaveTo="transform scale-y-95 opacity-0"
                  unmount={false}
                >
                  <Disclosure.Panel static className={panelClassNames}>
                    {children}
                  </Disclosure.Panel>
                </Transition>
              </>
            );
          }}
        </Disclosure>
      </div>
    );
  },
);
AccordionComponent.displayName = 'AccordionComponent';
export default AccordionComponent;
